import React from "react";
import { Box, Flex, Tag, Text, Spinner } from "@chakra-ui/react";
import { t } from "i18next";
import { useSearchSuggest } from "../../hooks/useSearchSuggest";
import { useInstantSearch } from "../../hooks/useInstantSearch";
import { useSlideInMenu } from "../../../../components/slideInMenu/hooks/useSlideInMenu";
import { SectionHeader } from "./SectionHeader";
import { CreatorMiniCard } from "./CreatorMiniCard";
import { ResultRow } from "./ResultRow";
import type {
  SearchContext,
  SearchSuggestItem,
} from "../../types";
import {
  buildSearchNavigationContext,
  getTypedSearchResultPosition,
  storeSearchNavigationContext,
} from "./searchNavigationContext";

interface TypingContentProps {
  query: string;
  context?: SearchContext;
  channelFilters?: Record<string, number>;
  onNavigate: (path: string) => void;
  onSelectSuggestion: (name: string) => void;
}

function SectionDivider() {
  return <Box h="1px" bg="transparent.white.10" />;
}
function ViewAllButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <Flex justify="center" mt={3}>
      <Flex
        as="button"
        align="center"
        justify="center"
        bg="transparent.white.10"
        borderRadius="12px"
        px={4}
        py="6px"
        cursor="pointer"
        onClick={onClick}
        _hover={{ bg: "transparent.white.20" }}
      >
        <Text
          fontSize="14px"
          fontWeight="500"
          fontFamily="Roboto"
          color="neutral.300"
          letterSpacing="0.07px"
        >
          {label}
        </Text>
      </Flex>
    </Flex>
  );
}

const DEFAULT_ORDER = ["series", "releases", "creators"];

export function TypingContent({
  query,
  context,
  channelFilters,
  onNavigate,
  onSelectSuggestion,
}: TypingContentProps) {
  // Suggestions always search all indexes (typeahead should show everything)
  const { data: suggestions } = useSearchSuggest(query);
  const { data: results, isLoading } = useInstantSearch(query, context, channelFilters);
  const { openComicInfoMenu, openArtistProfile } = useSlideInMenu();

  const series = results?.series;
  const releases = results?.releases;
  const creators = results?.creators;

  const hasSuggestions = suggestions && suggestions.length > 0;
  const hasResults = series?.items.length || releases?.items.length || creators?.items.length;

  if (isLoading && !hasResults && !hasSuggestions) {
    return (
      <Flex justify="center" align="center" p={8}>
        <Spinner color="blaze.blaze" size="md" />
      </Flex>
    );
  }

  if (!hasSuggestions && !hasResults && !isLoading) {
    return (
      <Flex justify="center" p={6}>
        <Text fontSize="14px" color="neutral.400">
          {t("screens.search.noResultsFound")}
        </Text>
      </Flex>
    );
  }

  const hasCreators = creators && creators.items.length > 0;
  const hasSeries = series && series.items.length > 0;
  const hasReleases = releases && releases.items.length > 0;
  const visibleSeries = series?.items.slice(0, 3) ?? [];
  const visibleReleases = releases?.items.slice(0, 3) ?? [];

  const sectionOrder = results?.display_order ?? DEFAULT_ORDER;
  const sectionCounts: Record<string, number> = {
    creators: creators?.items.length ?? 0,
    series: visibleSeries.length,
    releases: visibleReleases.length,
  };

  const renderCreators = () =>
    hasCreators ? (
      <Flex direction="column">
        <SectionHeader title={t("screens.search.creators")} />
        <Flex gap={3} overflowX="auto" pb={1}>
          {creators.items.map((item) => (
            <CreatorMiniCard
              key={item.id}
              name={item.roman_name || item.name}
              avatarUrl={item.avatar_small_url || item.avatar_url}
              avatarOnly
              onClick={() => openArtistProfile(item.id)}
            />
          ))}
        </Flex>
      </Flex>
    ) : null;

  const renderSeries = () =>
    hasSeries ? (
      <Flex direction="column">
        <SectionHeader title={t("screens.search.series")} />
        {visibleSeries.map((item, index) => (
          <ResultRow
            key={item.id}
            coverUrl={item.cover_image_url}
            title={item.name}
            subtitle={[
              item.releases_count_string ?? `${item.total_releases} issues`,
              item.artist_name,
            ].join(" \u2022 ")}
            onClick={() =>
              openComicInfoMenu(
                item.slug,
                undefined,
                buildSearchNavigationContext(
                  query,
                  getTypedSearchResultPosition({
                    section: "series",
                    index,
                    sectionOrder,
                    sectionCounts,
                  }),
                ),
              )}
          />
        ))}
        <ViewAllButton
          label={t("screens.search.viewAllSeriesResults")}
          onClick={() => onNavigate(`/browse?q=${encodeURIComponent(query)}`)}
        />
      </Flex>
    ) : null;

  const renderReleases = () =>
    hasReleases ? (
      <Flex direction="column">
        <SectionHeader title={t("screens.search.releases")} />
        {visibleReleases.map((item, index) => (
          <ResultRow
            key={item.id}
            coverUrl={item.thumbnail_url ?? undefined}
            title={item.title}
            subtitle={item.comic_name || item.title}
            onClick={() => {
              storeSearchNavigationContext(
                query,
                getTypedSearchResultPosition({
                  section: "releases",
                  index,
                  sectionOrder,
                  sectionCounts,
                }),
              );
              onNavigate(`/read/${item.key}`);
            }}
          />
        ))}
        <ViewAllButton
          label={t("screens.search.viewAllReleaseResults")}
          onClick={() => onNavigate(`/books?q=${encodeURIComponent(query)}`)}
        />
      </Flex>
    ) : null;

  const renderers: Record<string, () => React.ReactNode> = {
    creators: renderCreators,
    series: renderSeries,
    releases: renderReleases,
  };

  const renderedSections = sectionOrder
    .map((key) => ({ key, node: renderers[key]() }))
    .filter((s) => s.node != null);

  return (
    <Flex direction="column" gap={4} p={5}>
      {/* Suggestions */}
      {hasSuggestions && (
        <Flex direction="column">
          <Flex gap={2} flexWrap="wrap">
            {(suggestions as SearchSuggestItem[]).map((item) => (
              <Tag
                key={`${item.type}-${item.id}`}
                bg="transparent.white.10"
                borderRadius="full"
                px="12px"
                py="8px"
                fontSize="14px"
                fontWeight="500"
                fontFamily="Roboto"
                color="neutral.100"
                letterSpacing="0.5px"
                cursor="pointer"
                justifyContent="center"
                _hover={{ bg: "transparent.white.20" }}
                onClick={() => onSelectSuggestion(item.name)}
              >
                {item.name}
              </Tag>
            ))}
          </Flex>
        </Flex>
      )}

      {renderedSections.map(({ key, node }, i) => (
        <Flex direction="column" key={key} gap={4}>
          {(i > 0 || hasSuggestions) && <SectionDivider />}
          {node}
        </Flex>
      ))}
    </Flex>
  );
}
